import { LocationSettingsRequest } from '@weave/schema-gen-ts/dist/schemas/insurance-verification/v1/insurance_verification_adapter.pb';
import { State } from '@weave/schema-gen-ts/dist/schemas/platform-location-feature/v1/platform-enums/location_feature_enum.pb';
import { Feature } from '@weave/schema-gen-ts/dist/shared/feature/location_feature.pb';
import { Vertical } from '@weave/schema-gen-ts/dist/shared/vertical/vertical.pb';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess';
import { CustomizationFlagQueries } from '@frontend/api-customization-flags';
import { submitFeedback, WIVFeedbackRequest } from '@frontend/api-verify-signup';
import { hasSchemaACL } from '@frontend/auth-helpers';
import { useLocalizedQuery } from '@frontend/location-helpers';
import { useMutation } from '@frontend/react-query-helpers';
import { SchemaInsuranceVerificationAdapterServiceV1 } from '@frontend/schema';
import { useAppScopeStore, useScopedAppFlagStore } from '@frontend/scope';
import { queryKeys } from './query-keys';

export const useGetWeaveInsuranceVerificationCustomizationFlagDetails = () => {
  const { selectedLocationIds, accessibleLocationData } = useAppScopeStore();

  // Check if the selected location is a dental office for multi location or single location
  // since a multi location will be under the same vertical so checking the first location would help us to identify the vertical
  const isDental = accessibleLocationData[selectedLocationIds[0]]?.vertical === Vertical.DENTAL;

  const getIsWIVDentalLocation = (locationId: string) => {
    return accessibleLocationData[locationId]?.vertical === Vertical.DENTAL;
  };

  const {
    isLoading: isLoadingWeaveVerifyFeature,
    isFeatureHiddenInAllLocations: isFeatureHiddenInAllLocation,
    locationIdWiseCustomizationFlagDetails,
  } = CustomizationFlagQueries.useAggregateCustomizationFlagDetails({
    locationIds: selectedLocationIds,
    enabled: !!selectedLocationIds.length && isDental,
    customizationFlag: Feature.WEAVE_VERIFY,
    activeStates: [State.ACTIVE], // Providing access to Active flag only
  });

  const wivLocationIds = Object.keys(locationIdWiseCustomizationFlagDetails).filter(
    (locationId) => locationIdWiseCustomizationFlagDetails[locationId]
  );

  const isAnyLocationDental = wivLocationIds?.some(
    (locationId) => accessibleLocationData[locationId]?.vertical === Vertical.DENTAL
  );

  return {
    isLoadingWeaveVerifyFeature,
    isFeatureHiddenInAllLocation,
    locationIdWiseCustomizationFlagDetails,
    isDental,
    isAnyLocationDental,
    getIsWIVDentalLocation,
  };
};

export const useGetWeaveInsuranceVerificationFlag = (flag: 'weaveverify' | 'weaveverifytrial') => {
  const { getCustomizationFlagValue } = useScopedAppFlagStore();
  const hasWeaveVerifyAccess = getCustomizationFlagValue(flag);
  return hasWeaveVerifyAccess;
};

export const useSubmitFeedback = (locationId: string, userId: string) => {
  return useMutation((feedbackDetails: WIVFeedbackRequest) => submitFeedback(feedbackDetails), {
    mutationKey: queryKeys.feedback(locationId, userId),
    retry: false,
  });
};

export const useGetLocationSettingsDetails = (locationId: string, enabled = true) => {
  return useLocalizedQuery({
    queryFn: () =>
      SchemaInsuranceVerificationAdapterServiceV1.GetLocationSettings({}, { headers: { 'Location-Id': locationId } }),
    enabled: enabled,
  });
};

export const useSetLocationSettingsDetails = (locationId: string) => {
  return useMutation((req: LocationSettingsRequest) =>
    SchemaInsuranceVerificationAdapterServiceV1.SaveLocationSettings(req, { headers: { 'Location-Id': locationId } })
  );
};

export const useHasInsuranceVerificationACLAccess = () => {
  const { selectedLocationIds } = useAppScopeStore();
  const hasAccessToInsuranceVerification = selectedLocationIds.some((locationId) =>
    hasSchemaACL(locationId, [Permission.INSURANCE_VERIFICATION_READ, Permission.INSURANCE_VERIFICATION_WRITE])
  );
  return { hasAccessToInsuranceVerification };
};

export const useGetWeaveEnhancedInsuranceVerificationCustomizationFlagDetails = () => {
  const { selectedLocationIds, accessibleLocationData } = useAppScopeStore();

  // Check if the selected location is a dental office for multi location or single location
  // since a multi location will be under the same vertical so checking the first location would help us to identify the vertical
  const isDental = accessibleLocationData[selectedLocationIds[0]]?.vertical === Vertical.DENTAL;
  const isWellnessVertical = accessibleLocationData[selectedLocationIds[0]]?.vertical === Vertical.WELLNESS;

  const getIsEIVDentalLocation = (locationId: string) => {
    return accessibleLocationData[locationId]?.vertical === Vertical.DENTAL;
  };

  const {
    isLoading: isLoadingWeaveVerifyFeature,
    isFeatureHiddenInAllLocations: isFeatureHiddenInAllLocation,
    locationIdWiseCustomizationFlagDetails,
  } = CustomizationFlagQueries.useAggregateCustomizationFlagDetails({
    locationIds: selectedLocationIds,
    enabled: !!selectedLocationIds.length && isDental,
    customizationFlag: Feature.ENHANCED_INSURANCE_VERIFICATION,
    activeStates: [State.ACTIVE, State.TRIAL], // Providing access to Active and Trial states
  });

  const eivLocationIds = Object.keys(locationIdWiseCustomizationFlagDetails).filter(
    (locationId) => locationIdWiseCustomizationFlagDetails[locationId]
  );

  const isAnyLocationDental = eivLocationIds?.some(
    (locationId) => accessibleLocationData[locationId]?.vertical === Vertical.DENTAL
  );

  return {
    isLoadingWeaveVerifyFeature,
    isFeatureHiddenInAllLocation,
    locationIdWiseCustomizationFlagDetails,
    isDental,
    isWellnessVertical,
    isAnyLocationDental,
    getIsEIVDentalLocation,
  };
};

export const useHasWIVOrEIVAccess = () => {
  const {
    isLoadingWeaveVerifyFeature: isLoadingWIVFeature,
    isFeatureHiddenInAllLocation: isWIVFeatureHiddenInAllLocation,
    locationIdWiseCustomizationFlagDetails: locationIdWiseWIVCustomizationFlagDetails,
    isAnyLocationDental: isAnyWIVLocationDental,
    getIsWIVDentalLocation,
  } = useGetWeaveInsuranceVerificationCustomizationFlagDetails();

  const {
    isLoadingWeaveVerifyFeature: isLoadingEIVFeature,
    isFeatureHiddenInAllLocation: isEIVFeatureHiddenInAllLocation,
    locationIdWiseCustomizationFlagDetails: locationIdWiseEIVCustomizationFlagDetails,
    isAnyLocationDental: isAnyEIVLocationDental,
    isWellnessVertical,
    getIsEIVDentalLocation,
  } = useGetWeaveEnhancedInsuranceVerificationCustomizationFlagDetails();

  const isLoading = isLoadingWIVFeature || isLoadingEIVFeature;

  const hasInsuranceVerificationAccess = !isWIVFeatureHiddenInAllLocation || !isEIVFeatureHiddenInAllLocation;

  // If both flags are enabled for a location, we can consider that location as dental
  const isDental = isAnyWIVLocationDental || isAnyEIVLocationDental;

  const getHasInsuranceVerificationAccessForLocation = (locID: string) =>
    locationIdWiseWIVCustomizationFlagDetails?.[locID] || locationIdWiseEIVCustomizationFlagDetails?.[locID] || false;

  return {
    isLoading,
    isEIVFeatureHiddenInAllLocation,
    isWIVFeatureHiddenInAllLocation,
    locationIdWiseEIVCustomizationFlagDetails,
    locationIdWiseWIVCustomizationFlagDetails,
    hasInsuranceVerificationAccess,
    isDental,
    isWellnessVertical,
    getIsWIVDentalLocation,
    getIsEIVDentalLocation,
    getHasInsuranceVerificationAccessForLocation,
  };
};

export const useShouldShowEIVInsuranceComponentsFromRoutes = () => {
  const { hasAccessToInsuranceVerification } = useHasInsuranceVerificationACLAccess();
  const { isAnyLocationDental, isFeatureHiddenInAllLocation } =
    useGetWeaveEnhancedInsuranceVerificationCustomizationFlagDetails();
  const shouldShowInsuranceComponentsFromRoutes =
    !isFeatureHiddenInAllLocation && isAnyLocationDental && hasAccessToInsuranceVerification;
  return shouldShowInsuranceComponentsFromRoutes;
};
